/**
 * Deterministic cart drawer upsells backed by Shopify cart JSON, line keys,
 * final prices, discount allocations, and bundled section HTML.
 */
(function initializeCartDrawerUpsell(global) {
  if (!global?.customElements || !global.HTMLElement || !global.CartUpsellState) return;

  const mutationQueue = global.CartUpsellState.getSharedCartMutationQueue();

  class CartDrawerUpsell extends global.HTMLElement {
    static autoAddAttempted = new Set();
    static orphanCleanupPending = false;

    constructor() {
      super();
      this.lifecycle = global.CartUpsellState.createConnectionLifecycle();
    }

    connectedCallback() {
      const controller = this.lifecycle.connect();
      this.cartDrawer = global.document.querySelector('cart-drawer');
      this.initializedSignal = null;

      if (this.dataset.url) {
        this.loadRecommendations(this.dataset.url, controller);
      } else {
        this.initializeConnection(controller);
      }
    }

    disconnectedCallback() {
      this.lifecycle.disconnect();
      this.initializedSignal = null;
    }

    async loadRecommendations(url, controller) {
      try {
        const response = await global.fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`Recommendations request failed (HTTP ${response.status}).`);
        const html = await response.text();
        if (controller.signal.aborted || !this.isConnected) return;

        const parsed = new global.DOMParser().parseFromString(html, 'text/html');
        const returnedUpsell = parsed.getElementById(this.id);
        if (returnedUpsell) this.innerHTML = returnedUpsell.innerHTML;
        this.initializeConnection(controller);
      } catch (error) {
        if (error?.name === 'AbortError') return;
        this.showError(error);
        console.error('[cart-upsell] recommendations request failed', error);
      }
    }

    initializeConnection(controller) {
      if (controller.signal.aborted || this.initializedSignal === controller.signal) return;
      this.initializedSignal = controller.signal;
      this.resolveVariants();
      this.resolveCartState();
      this.wireVariantSelects(controller.signal);
      this.wireAddControls(controller.signal);
      this.wireToggleControls(controller.signal);

      const unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.cartData && Array.isArray(event.cartData.items)) this.cartState = event.cartData;
        if (this.dataset.hideInCart === 'true' && this.dataset.style === 'add_button') {
          this.hideInCartItems(event?.cartData);
        }
        this.removeOrphanedUpsells(event);
      });
      controller.signal.addEventListener('abort', unsubscribe, { once: true });
    }

    resolveVariants() {
      for (const card of this.querySelectorAll('.upsell')) {
        const json = card.querySelector('.upsell__variants-json');
        try {
          card.variants = json ? JSON.parse(json.textContent) : [];
        } catch (_) {
          card.variants = [];
        }
      }
    }

    resolveCartState() {
      const json = this.querySelector('.upsell__cart-json');
      try {
        const items = json ? JSON.parse(json.textContent) : [];
        this.cartState = { items: Array.isArray(items) ? items : [] };
      } catch (_) {
        this.cartState = { items: [] };
      }
    }

    wireVariantSelects(signal) {
      for (const card of this.querySelectorAll('.upsell')) {
        const picker = card.querySelector('.upsell__variant-picker');
        if (!picker || card.variants.length === 0) continue;

        picker.addEventListener('change', () => {
          const selectedOptions = Array.from(
            picker.querySelectorAll('.upsell__select__input'),
            (select) => select.value,
          );
          const variant = card.variants.find((candidate) => {
            const options = [candidate.option1, candidate.option2, candidate.option3]
              .slice(0, selectedOptions.length);
            return options.every((option, index) => option === selectedOptions[index]);
          });

          if (!variant) return;
          this.syncCardVariant(card, variant);
        }, { signal });
      }
    }

    syncCardVariant(card, variant) {
      const line = this.resolveCardLine(this.cartState, card, variant.id);
      card.dataset.id = String(variant.id);
      if (line?.key) card.dataset.lineKey = line.key;
      else delete card.dataset.lineKey;

      const idInput = card.querySelector('.upsell__hidden-form input[name="id"]');
      if (idInput) idInput.value = variant.id;

      const sellingPlanInput = card.querySelector('.upsell__hidden-form input[name="selling_plan"]');
      if (sellingPlanInput) {
        sellingPlanInput.value = variant.selling_plan_allocations?.[0]?.selling_plan?.id || '';
      }

      const addButton = card.querySelector('.upsell__add-btn');
      if (addButton) {
        addButton.dataset.id = String(variant.id);
        addButton.disabled = !variant.available || Boolean(line && this.dataset.hideInCart === 'true');
      }

      const pricing = global.CartUpsellState.resolveUpsellPricing({ variant, line });
      const moneyFormat = card.dataset.moneyFormat || '{{amount}}';

      for (const element of card.querySelectorAll('.upsell__price .regular-price')) {
        element.textContent = CartDrawerUpsell.formatMoney(pricing.finalPrice, moneyFormat);
      }
      for (const element of card.querySelectorAll('.upsell__price .compare-price')) {
        element.textContent = pricing.comparePrice
          ? CartDrawerUpsell.formatMoney(pricing.comparePrice, moneyFormat)
          : '';
        element.classList.toggle('hidden', !pricing.comparePrice);
      }

      this.renderDiscountAllocations(card, pricing.discountAllocations);
      if (card.dataset.toggle === 'true') this.setToggleState(card, Boolean(line), false);
    }

    renderDiscountAllocations(card, allocations) {
      const list = card.querySelector('.upsell__discounts');
      if (!list) return;
      list.replaceChildren();

      for (const allocation of allocations) {
        const title = allocation?.discount_application?.title;
        if (!title) continue;
        const item = global.document.createElement('li');
        item.className = 'discounts__discount';
        item.textContent = title;
        list.append(item);
      }
      list.hidden = list.children.length === 0;
    }

    wireAddControls(signal) {
      for (const button of this.querySelectorAll('.upsell__add-btn[data-action="add"]')) {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const card = button.closest('.upsell');
          if (card) this.addToCart(card, button);
        }, { signal });
      }
    }

    async addToCart(card, button, isAutoAdd = false) {
      if (button?.disabled || card.dataset.busy === 'true') return;
      const variantId = card.dataset.id;
      let mutationCommitted = false;
      this.setBusy(card, true);
      if (button) this.setAddButtonBusy(button, true);

      try {
        await this.ensureCartDrawer();
        const sectionContext = this.getSectionContext();
        const result = await mutationQueue.enqueue({
          mutate: () => this.requestAdd(card, variantId, sectionContext.sectionIds),
          reconcile: (addedItem) => this.reconcileAdd(addedItem, sectionContext.sectionIds),
          repaint: (cartState) => this.renderCart(cartState, sectionContext),
          publish: (cartState, outcome) => {
            if (outcome.reconciliationError) return this.publishRecovery(variantId);
            return this.publishCart(cartState, variantId, outcome.rendered);
          },
        });
        mutationCommitted = result.mutationCommitted;
        if (result.mutationResponse?.key) card.dataset.lineKey = result.mutationResponse.key;
        if (!result.reconciliationError && Array.isArray(result.response?.items)) {
          this.cartState = result.response;
        }
        if (result.reconciliationError || result.repaintError || result.publishError) this.showError();
        return result;
      } catch (error) {
        if (isAutoAdd) CartDrawerUpsell.autoAddAttempted.delete(String(variantId));
        if (card.dataset.toggle === 'true') this.setToggleState(card, false, false);
        this.showError(error);
        console.error('[cart-upsell] add failed', error);
      } finally {
        this.setBusy(card, false);
        if (button) {
          this.setAddButtonBusy(button, false);
          if (
            mutationCommitted &&
            card.dataset.toggle !== 'true' &&
            this.dataset.hideInCart === 'true'
          ) {
            button.disabled = true;
            button.setAttribute('aria-disabled', 'true');
          }
        }
      }
    }

    wireToggleControls(signal) {
      if (this.dataset.style === 'add_button') return;

      for (const card of this.querySelectorAll('.upsell[data-toggle="true"]')) {
        const variantId = card.dataset.id;
        const line = this.resolveCardLine(this.cartState, card, variantId);
        const preselected = card.dataset.selected === 'true';
        const cartIsEmpty = this.cartState.items.length === 0;

        if (line) {
          card.dataset.lineKey = line.key;
          this.setToggleState(card, true, false);
        } else if (
          preselected &&
          !cartIsEmpty &&
          !this.isDismissed(variantId) &&
          !CartDrawerUpsell.autoAddAttempted.has(String(variantId))
        ) {
          CartDrawerUpsell.autoAddAttempted.add(String(variantId));
          this.setToggleState(card, true, false);
          this.addToCart(card, null, true);
        } else {
          this.setToggleState(card, false, false);
        }

        const toggle = card.querySelector('.upsell-toggle-btn') || card.querySelector('.upsell__container');
        if (!toggle) continue;
        toggle.addEventListener('click', (event) => {
          if (event.target.closest('.upsell__variant-picker, a[href], select, option, label')) return;
          if (card.dataset.busy === 'true') return;

          const selected = card.dataset.currentlySelected === 'true';
          this.setToggleState(card, !selected, false);
          if (selected) {
            this.setDismissed(card.dataset.id);
            this.removeFromCart(card);
          } else {
            this.clearDismissed(card.dataset.id);
            this.addToCart(card);
          }
        }, { signal });
      }
    }

    setToggleState(card, selected, _triggerCartUpdate) {
      card.dataset.currentlySelected = String(selected);
      card.classList.toggle('upsell--selected', selected);

      const button = card.querySelector(
        '.upsell__toggle-switch, .upsell__checkbox, .upsell__plus-btn',
      );
      if (!button) return;
      button.setAttribute('aria-pressed', String(selected));
      button.setAttribute('aria-label', selected ? button.dataset.removeLabel : button.dataset.addLabel);
      for (const select of card.querySelectorAll('.upsell__select__input')) {
        select.disabled = selected || card.dataset.busy === 'true';
      }
    }

    async removeFromCart(card) {
      this.setBusy(card, true);
      const variantId = card.dataset.id;

      try {
        await this.ensureCartDrawer();
        const sectionContext = this.getSectionContext();
        const result = await mutationQueue.enqueue({
          mutate: async () => {
            const currentCart = await this.fetchCart();
            const line = global.CartUpsellState.resolveOwnedUpsellLine(
              currentCart,
              {
                ownerKey: card.dataset.ownerKey,
                preferredLineKey: card.dataset.lineKey,
                variantId,
              },
            );
            if (!line) return this.fetchCartSections(currentCart, sectionContext.sectionIds);

            const payload = global.CartUpsellState.createLineChangePayload({
              line,
              quantity: Math.max(0, line.quantity - 1),
              sections: sectionContext.sectionIds.slice(0, 5),
              sectionsUrl: global.location.pathname,
            });
            return this.requestCartChange(payload);
          },
          reconcile: (cartState) => this.fetchCartSections(cartState, sectionContext.sectionIds),
          repaint: (cartState) => this.renderCart(cartState, sectionContext),
          publish: (cartState, outcome) => {
            if (outcome.reconciliationError) return this.publishRecovery(variantId);
            return this.publishCart(cartState, variantId, outcome.rendered);
          },
        });
        this.cartState = result.response;
        if (result.reconciliationError || result.repaintError || result.publishError) this.showError();
      } catch (error) {
        this.setToggleState(card, true, false);
        this.showError(error);
        console.error('[cart-upsell] remove failed', error);
      } finally {
        this.setBusy(card, false);
      }
    }

    removeOrphanedUpsells(event) {
      if (event?.source === 'cart-upsell' || CartDrawerUpsell.orphanCleanupPending) return;
      const cartState = event?.cartData;
      if (!Array.isArray(cartState?.items) || cartState.items.length === 0) return;

      const ownerKeys = new Set();
      const activeUpsells = new Set([
        this,
        ...global.document.querySelectorAll('cart-drawer-upsell'),
      ]);
      for (const upsell of activeUpsells) {
        if (!upsell?.isConnected) continue;
        for (const card of upsell.querySelectorAll('.upsell[data-toggle="true"]')) {
          if (card.dataset.ownerKey) ownerKeys.add(card.dataset.ownerKey);
        }
      }
      const ownedLines = global.CartUpsellState.findOwnedUpsellLines(cartState, ownerKeys);
      if (ownedLines.length === 0 || ownedLines.length !== cartState.items.length) return;

      CartDrawerUpsell.orphanCleanupPending = true;
      this.clearOrphans(ownedLines).finally(() => {
        CartDrawerUpsell.orphanCleanupPending = false;
      });
    }

    async clearOrphans(lines) {
      try {
        await this.ensureCartDrawer();
        const sectionContext = this.getSectionContext();
        const updates = Object.fromEntries(lines.map((line) => [line.key, 0]));
        const result = await mutationQueue.enqueue({
          mutate: async () => {
            return this.requestCartUpdate({
              updates,
              sections: sectionContext.sectionIds.slice(0, 5),
              sections_url: global.location.pathname,
            });
          },
          reconcile: (cartState) => this.fetchCartSections(cartState, sectionContext.sectionIds),
          repaint: (cartState) => this.renderCart(cartState, sectionContext),
          publish: (cartState, outcome) => {
            if (outcome.reconciliationError) return this.publishRecovery();
            return this.publishCart(cartState, undefined, outcome.rendered);
          },
        });

        for (const line of lines) {
          CartDrawerUpsell.autoAddAttempted.delete(String(line.variant_id));
        }
        this.cartState = result.response;
        if (result.reconciliationError || result.repaintError || result.publishError) this.showError();
      } catch (error) {
        this.showError(error);
        console.error('[cart-upsell] orphan cleanup failed', error);
      }
    }

    hideInCartItems(cartState) {
      if (!Array.isArray(cartState?.items)) return;
      let visibleCount = 0;

      for (const card of this.querySelectorAll('.upsell')) {
        const productId = String(card.dataset.productId || '');
        const hidden = productId !== '' && cartState.items.some(
          (item) => String(item.product_id) === productId,
        );
        card.hidden = hidden;
        if (!hidden) visibleCount += 1;
      }
      this.hidden = visibleCount === 0;
    }

    async requestAdd(card, variantId, sectionIds = this.sectionIds()) {
      const formData = new global.FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      if (card.dataset.toggle === 'true' && card.dataset.ownerKey) {
        formData.append(
          `properties[${global.CartUpsellState.UPSELL_OWNER_PROPERTY}]`,
          card.dataset.ownerKey,
        );
      }
      if (card.dataset.toggle === 'true' && card.dataset.cartHidden === 'true') {
        formData.append('properties[_cart_hidden]', 'true');
      }
      const sellingPlan = card.querySelector('.upsell__hidden-form input[name="selling_plan"]')?.value;
      if (sellingPlan) formData.append('selling_plan', sellingPlan);
      formData.append('sections', sectionIds.slice(0, 5).join(','));
      formData.append('sections_url', global.location.pathname);

      return this.readJsonResponse(await global.fetch(global.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      }));
    }

    async reconcileAdd(addedItem, sectionIds = this.sectionIds()) {
      const cartState = await this.fetchCart();
      return this.fetchCartSections({
        ...cartState,
        id: addedItem.id,
        sections: addedItem.sections,
      }, sectionIds);
    }

    async requestCartChange(payload) {
      return this.readCartResponse(await global.fetch(global.routes.cart_change_url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/javascript',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      }));
    }

    async requestCartUpdate(payload) {
      return this.readCartResponse(await global.fetch(global.routes.cart_update_url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/javascript',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      }));
    }

    async fetchCart() {
      return this.readCartResponse(await global.fetch(`${global.routes.cart_url}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      }), false);
    }

    async fetchCartSections(cartState, sectionIds = this.sectionIds()) {
      const sections = { ...(cartState.sections || {}) };
      const missingSectionIds = Array.from(new Set(sectionIds)).filter((sectionId) => (
        typeof sections[sectionId] !== 'string'
      ));

      for (let index = 0; index < missingSectionIds.length; index += 5) {
        const batch = missingSectionIds.slice(index, index + 5);
        const url = new global.URL(global.routes.cart_url, global.location.origin);
        url.searchParams.set('sections', batch.join(','));
        url.searchParams.set('sections_url', global.location.pathname);
        const response = await global.fetch(url, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        Object.assign(sections, await this.readJsonResponse(response));
      }

      return { ...cartState, sections };
    }

    async readCartResponse(response, requireSections = true) {
      const cartState = await this.readJsonResponse(response);
      if (!Array.isArray(cartState.items) || !Number.isFinite(cartState.item_count)) {
        throw new Error('Shopify did not return authoritative cart state.');
      }
      if (requireSections && (!cartState.sections || typeof cartState.sections !== 'object')) {
        throw new Error('Shopify did not return updated cart sections.');
      }
      return cartState;
    }

    async readJsonResponse(response) {
      let state;
      try {
        state = await response.json();
      } catch (_) {
        throw new Error(`Cart request returned invalid JSON (HTTP ${response.status}).`);
      }
      if (!response.ok || state?.status || state?.errors) {
        throw new Error(state?.description || state?.message || `Cart request failed (HTTP ${response.status}).`);
      }
      return state;
    }

    resolveCardLine(cartState, card, variantId) {
      if (card.dataset.toggle === 'true') {
        return global.CartUpsellState.resolveOwnedUpsellLine(cartState, {
          ownerKey: card.dataset.ownerKey,
          preferredLineKey: card.dataset.lineKey,
          variantId,
        });
      }
      return global.CartUpsellState.resolveUpsellLine(
        cartState,
        variantId,
        card.dataset.lineKey,
      );
    }

    getSectionContext() {
      const drawerDescriptors = this.cartDrawer.getSectionsToRender();
      const mainCartItems = global.document.querySelector('cart-items');
      const mainCartDescriptors = mainCartItems?.getSectionsToRender?.() || [];
      const sectionIds = [];

      for (const descriptor of [...drawerDescriptors, ...mainCartDescriptors]) {
        const sectionId = descriptor.section || descriptor.id;
        if (sectionId && !sectionIds.includes(sectionId)) sectionIds.push(sectionId);
      }

      return {
        drawerDescriptors,
        mainCartDescriptors,
        mainCartItems,
        sectionIds,
      };
    }

    sectionIds() {
      return this.getSectionContext().sectionIds;
    }

    async ensureCartDrawer() {
      if (!this.cartDrawer) throw new Error('Cart drawer is unavailable.');
      if (typeof this.cartDrawer.getSectionsToRender === 'function') return;
      await global.customElements.whenDefined('cart-drawer');
    }

    renderCart(cartState, sectionContext = this.getSectionContext()) {
      if (!cartState.sections) throw new Error('Updated cart section HTML is unavailable.');
      global.CartUpsellState.validateReturnedSections(
        cartState.sections,
        sectionContext.sectionIds,
      );
      this.cartDrawer.classList.toggle('is-empty', cartState.item_count === 0);
      this.cartDrawer.renderContents(cartState);

      const { mainCartItems, mainCartDescriptors } = sectionContext;
      if (!mainCartItems) return;

      for (const descriptor of mainCartDescriptors) {
        if (descriptor.id === 'cart-icon-bubble') continue;
        const sectionId = descriptor.section || descriptor.id;
        const html = cartState.sections[sectionId];
        if (typeof html !== 'string') continue;
        const sectionElement = global.document.getElementById(descriptor.id);
        if (!sectionElement) continue;
        const elementToReplace = descriptor.selector
          ? sectionElement.querySelector(descriptor.selector) || sectionElement
          : sectionElement;
        elementToReplace.innerHTML = mainCartItems.getSectionInnerHTML(html, descriptor.selector);
      }
    }

    publishCart(cartState, variantId, sectionsRendered) {
      return publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-upsell',
        productVariantId: variantId,
        cartData: cartState,
        sectionsRendered,
      });
    }

    publishRecovery(variantId) {
      return publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-upsell',
        productVariantId: variantId,
        sectionsRendered: false,
      });
    }

    setBusy(card, busy) {
      card.dataset.busy = String(busy);
      card.setAttribute('aria-busy', String(busy));
      const control = card.querySelector(
        '.upsell__toggle-switch, .upsell__checkbox, .upsell__plus-btn',
      );
      if (control) {
        control.disabled = busy;
        control.setAttribute('aria-disabled', String(busy));
      }
      for (const select of card.querySelectorAll('.upsell__select__input')) {
        select.disabled = busy || card.dataset.currentlySelected === 'true';
      }
    }

    setAddButtonBusy(button, busy) {
      if (busy) button.dataset.wasDisabled = String(button.disabled);
      button.disabled = busy || button.dataset.wasDisabled === 'true';
      button.setAttribute('aria-disabled', String(button.disabled));
      button.classList.toggle('loading', busy);
      if (!busy) delete button.dataset.wasDisabled;
    }

    dismissKey(variantId) {
      return `upsell-dismissed-${variantId}`;
    }

    isDismissed(variantId) {
      try {
        return global.sessionStorage.getItem(this.dismissKey(variantId)) === '1';
      } catch (_) {
        return false;
      }
    }

    setDismissed(variantId) {
      try {
        global.sessionStorage.setItem(this.dismissKey(variantId), '1');
      } catch (_) {
        // Storage is optional; cart truth still comes from Shopify.
      }
    }

    clearDismissed(variantId) {
      try {
        global.sessionStorage.removeItem(this.dismissKey(variantId));
      } catch (_) {
        // Storage is optional; cart truth still comes from Shopify.
      }
    }

    showError(error) {
      const region = global.document.getElementById('CartDrawer-CartErrors');
      if (region) region.textContent = error?.message || this.dataset.errorMessage || '';
    }

    static formatMoney(centAmount, format) {
      const addThousands = (integer, separator) => integer.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
      const formats = {
        amount: [',', '.', 2, 100],
        amount_no_decimals: [',', '', 0, 100],
        amount_with_comma_separator: ['.', ',', 2, 100],
        amount_no_decimals_with_comma_separator: ['.', '', 0, 100],
        amount_with_space_separator: [' ', ',', 2, 100],
        amount_no_decimals_with_space_separator: [' ', '', 0, 100],
        amount_with_apostrophe_separator: ["'", '.', 2, 100],
        amount_with_period_and_space_separator: [' ', '.', 2, 100],
      };
      const negative = centAmount < 0;
      const total = Math.round(Math.abs(centAmount));
      let result = format;

      for (const [name, [thousands, decimal, decimals, divisor]] of Object.entries(formats)) {
        const integer = decimals === 0
          ? Math.round(total / divisor)
          : Math.floor(total / divisor);
        const fraction = total % divisor;
        let value = addThousands(integer.toString(), thousands);
        if (decimals > 0) value += decimal + fraction.toString().padStart(decimals, '0');
        if (negative) value = `-${value}`;
        result = result.replace(new RegExp(`\\{\\{\\s*${name}\\s*\\}\\}`, 'g'), value);
      }
      return result;
    }
  }

  if (!global.customElements.get('cart-drawer-upsell')) {
    global.customElements.define('cart-drawer-upsell', CartDrawerUpsell);
  }
})(typeof window !== 'undefined' ? window : globalThis);
